import { 
  Supplier, 
  RawMaterial, 
  InventoryTransaction, 
  DashboardSummary,
  StockLevel
} from '../types/warehouse';
import { API_BASE_URL as API_URL } from '../components/config/api';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`
});

// Suppliers API
export const suppliersAPI = {
  getAll: async (storeId: number, active?: boolean): Promise<Supplier[]> => {
    const params = active !== undefined ? `?active=${active}` : '';
    const response = await fetch(`${API_URL}/api/warehouse/stores/${storeId}/suppliers${params}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch suppliers');
    const data = await response.json();
    return data.suppliers;
  },

  getById: async (storeId: number, supplierId: number): Promise<Supplier> => {
    const response = await fetch(`${API_URL}/api/warehouse/stores/${storeId}/suppliers/${supplierId}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch supplier');
    const data = await response.json();
    return data.supplier;
  },

  create: async (storeId: number, supplier: Partial<Supplier>): Promise<Supplier> => {
    const response = await fetch(`${API_URL}/api/warehouse/stores/${storeId}/suppliers`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(supplier)
    });
    if (!response.ok) throw new Error('Failed to create supplier');
    const data = await response.json();
    return data.supplier;
  },

  update: async (storeId: number, supplierId: number, supplier: Partial<Supplier>): Promise<Supplier> => {
    const response = await fetch(`${API_URL}/api/warehouse/stores/${storeId}/suppliers/${supplierId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(supplier)
    });
    if (!response.ok) throw new Error('Failed to update supplier');
    const data = await response.json();
    return data.supplier;
  },

  delete: async (storeId: number, supplierId: number): Promise<void> => {
    const response = await fetch(`${API_URL}/api/warehouse/stores/${storeId}/suppliers/${supplierId}`, {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to delete supplier');
  }
};

// Raw Materials API
export const rawMaterialsAPI = {
  getAll: async (storeId: number, filters?: {
    active?: boolean;
    low_stock?: boolean;
    supplier_id?: number;
  }): Promise<RawMaterial[]> => {
    const params = new URLSearchParams();
    if (filters?.active !== undefined) params.append('active', filters.active.toString());
    if (filters?.low_stock !== undefined) params.append('low_stock', filters.low_stock.toString());
    if (filters?.supplier_id !== undefined) params.append('supplier_id', filters.supplier_id.toString());
    
    const response = await fetch(`${API_URL}/api/warehouse/stores/${storeId}/raw-materials?${params}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch raw materials');
    const data = await response.json();
    return data.rawMaterials;
  },

  getById: async (storeId: number, materialId: number) => {
    const response = await fetch(`${API_URL}/api/warehouse/stores/${storeId}/raw-materials/${materialId}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch raw material');
    return response.json();
  },

  create: async (storeId: number, material: Partial<RawMaterial>): Promise<RawMaterial> => {
    const response = await fetch(`${API_URL}/api/warehouse/stores/${storeId}/raw-materials`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(material)
    });
    if (!response.ok) throw new Error('Failed to create raw material');
    const data = await response.json();
    return data.rawMaterial;
  },

  update: async (storeId: number, materialId: number, material: Partial<RawMaterial>): Promise<RawMaterial> => {
    const response = await fetch(`${API_URL}/api/warehouse/stores/${storeId}/raw-materials/${materialId}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(material)
    });
    if (!response.ok) throw new Error('Failed to update raw material');
    const data = await response.json();
    return data.rawMaterial;
  },

  adjustStock: async (storeId: number, materialId: number, adjustment: {
    adjustment_type: 'ADJUSTMENT' | 'WASTE' | 'RETURN';
    quantity: number;
    reason?: string;
  }) => {
    const response = await fetch(`${API_URL}/api/warehouse/stores/${storeId}/raw-materials/${materialId}/adjust-stock`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(adjustment)
    });
    if (!response.ok) throw new Error('Failed to adjust stock');
    return response.json();
  }
};

// Inventory API
export const inventoryAPI = {
  getDashboard: async (storeId: number): Promise<DashboardSummary> => {
    const response = await fetch(`${API_URL}/api/warehouse/stores/${storeId}/inventory/dashboard`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch dashboard');
    const data = await response.json();
    return data.dashboard;
  },

  getTransactions: async (storeId: number, filters?: {
    transaction_type?: string;
    material_id?: number;
    date_from?: string;
    date_to?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ transactions: InventoryTransaction[], pagination: any }> => {
    const params = new URLSearchParams();
    if (filters?.transaction_type) params.append('transaction_type', filters.transaction_type);
    if (filters?.material_id) params.append('material_id', filters.material_id.toString());
    if (filters?.date_from) params.append('date_from', filters.date_from);
    if (filters?.date_to) params.append('date_to', filters.date_to);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());
    
    const response = await fetch(`${API_URL}/api/warehouse/stores/${storeId}/inventory/transactions?${params}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch transactions');
    return response.json();
  },

  recordPurchase: async (storeId: number, purchase: {
    items: Array<{
      raw_material_id: number;
      quantity: number;
      unit_price: number;
    }>;
    supplier_id?: number;
    reference_number?: string;
    notes?: string;
  }) => {
    const response = await fetch(`${API_URL}/api/warehouse/stores/${storeId}/inventory/purchase`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(purchase)
    });
    if (!response.ok) throw new Error('Failed to record purchase');
    return response.json();
  },

  getStockLevels: async (storeId: number, filters?: {
    category?: string;
    supplier_id?: number;
    status?: string;
  }): Promise<{ stockLevels: StockLevel[], summary: any }> => {
    const params = new URLSearchParams();
    if (filters?.category) params.append('category', filters.category);
    if (filters?.supplier_id) params.append('supplier_id', filters.supplier_id.toString());
    if (filters?.status) params.append('status', filters.status);
    
    const response = await fetch(`${API_URL}/api/warehouse/stores/${storeId}/inventory/stock-levels?${params}`, {
      headers: getAuthHeaders()
    });
    if (!response.ok) throw new Error('Failed to fetch stock levels');
    return response.json();
  }
};
